import { t } from '@lingui/core/macro';
import * as React from 'react';

import { TextField } from '../../../TextField';
import { formatTriggerDate, toIsoDay } from './format';
import { type DateLocale } from './localization';
import { parseSmartDate } from './parseSmartDate';

interface TypeableDateInputProps {
    /** Current ISO `yyyy-mm-dd` value (or `null`). */
    value: string | null;
    /** Called with a parsed date on Enter only: a blur commit would race a calendar click. */
    onCommit: (value: string) => void;
    locale: DateLocale;
    minDate?: Date;
    maxDate?: Date;
    autoFocus?: boolean;
}

const PLACEHOLDER_BY_LOCALE: Record<DateLocale, string> = {
    en: 'e.g. 04/15/2026, in 3 days, today',
    de: 'z. B. 15.04.2026, in 3 Tagen, heute',
    fr: 'p. ex. 15/04/2026, dans 3 jours, aujourd’hui',
    es: 'p. ej. 15/04/2026, en 3 días, hoy',
    it: 'es. 15/04/2026, in 3 giorni, oggi',
};

/** Takes locale formats, ISO and phrases such as `in 3 days`, and shows parse and bound errors inline. */
export function TypeableDateInput({
    value,
    onCommit,
    locale,
    minDate,
    maxDate,
    autoFocus,
}: TypeableDateInputProps): JSX.Element {
    const [draft, setDraft] = React.useState<string>(() => formatTriggerDate(value));
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
        setDraft(formatTriggerDate(value));
        setError(null);
    }, [value]);

    const tryCommit = React.useCallback(() => {
        const trimmed = draft.trim();
        if (!trimmed) {
            setError(null);
            return;
        }

        const parsed = parseSmartDate(trimmed, locale);
        if (!parsed) {
            setError(t`Could not parse "${trimmed}"`);
            return;
        }
        if (minDate && parsed < minDate) {
            setError(t`Date is before the allowed minimum`);
            return;
        }
        if (maxDate && parsed > maxDate) {
            setError(t`Date is after the allowed maximum`);
            return;
        }

        const iso = toIsoDay(parsed);
        if (iso !== value) {
            onCommit(iso);
        }
        setDraft(formatTriggerDate(iso));
        setError(null);
    }, [draft, locale, minDate, maxDate, value, onCommit]);

    return (
        <TextField
            size="small"
            fullWidth
            autoFocus={autoFocus}
            value={draft}
            placeholder={PLACEHOLDER_BY_LOCALE[locale]}
            onChange={(e) => {
                setDraft(e.target.value);
                if (error) setError(null);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    tryCommit();
                }
            }}
            error={Boolean(error)}
            helperText={error ?? undefined}
        />
    );
}
